/**
 * 役种定义表
 *
 * 番数规则参考：日本麻将（立直麻将）标准规则
 *   · 门清限定役（closedOnly）副露后不成立
 *   · 副露降番役（openHan）副露后番数减少
 */

import { defineYaku, type YakuDef } from './types.js';

// ════════════════════════════════════════════════════════════════
// 1 番役
// ════════════════════════════════════════════════════════════════

export const RIICHI = defineYaku({
  id: 'riichi',
  name: '立直',
  romaji: 'riichi',
  han: 1,
  category: 'riichi',
  level: 'normal',
  closedOnly: true,
  description: '门清听牌后宣言立直',
});

export const DOUBLE_RIICHI = defineYaku({
  id: 'double_riichi',
  name: '两立直',
  romaji: 'double riichi',
  han: 2,
  category: 'riichi',
  level: 'normal',
  closedOnly: true,
  description: '第一巡宣言立直',
});

export const IPPATSU = defineYaku({
  id: 'ippatsu',
  name: '一发',
  romaji: 'ippatsu',
  han: 1,
  category: 'riichi',
  level: 'normal',
  closedOnly: true,
  description: '立直后一巡内和牌',
});

export const MENZEN_TSUMO = defineYaku({
  id: 'menzen_tsumo',
  name: '门前清自摸和',
  romaji: 'menzen tsumo',
  han: 1,
  category: 'tsumo',
  level: 'normal',
  closedOnly: true,
  description: '门清状态下自摸和牌',
});

export const PINFU = defineYaku({
  id: 'pinfu',
  name: '平和',
  romaji: 'pinfu',
  han: 1,
  category: 'shape',
  level: 'normal',
  closedOnly: true,
  description: '全顺子 + 非役牌雀头 + 两面听',
});

export const TANYAO = defineYaku({
  id: 'tanyao',
  name: '断幺九',
  romaji: 'tanyao',
  han: 1,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '全部由中张牌（2-8）组成',
});

export const IIPEIKOU = defineYaku({
  id: 'iipeikou',
  name: '一盃口',
  romaji: 'iipeikou',
  han: 1,
  category: 'shape',
  level: 'normal',
  closedOnly: true,
  description: '两个相同的顺子',
});

export const YAKUHAI_HAKU = defineYaku({
  id: 'yakuhai_haku',
  name: '役牌 白',
  romaji: 'yakuhai haku',
  han: 1,
  category: 'honor',
  level: 'normal',
  closedOnly: false,
  description: '白的三元牌刻子',
});

export const YAKUHAI_HATSU = defineYaku({
  id: 'yakuhai_hatsu',
  name: '役牌 发',
  romaji: 'yakuhai hatsu',
  han: 1,
  category: 'honor',
  level: 'normal',
  closedOnly: false,
  description: '发的三元牌刻子',
});

export const YAKUHAI_CHUN = defineYaku({
  id: 'yakuhai_chun',
  name: '役牌 中',
  romaji: 'yakuhai chun',
  han: 1,
  category: 'honor',
  level: 'normal',
  closedOnly: false,
  description: '中的三元牌刻子',
});

export const YAKUHAI_ROUND = defineYaku({
  id: 'yakuhai_round',
  name: '场风牌',
  romaji: 'yakuhai round wind',
  han: 1,
  category: 'honor',
  level: 'normal',
  closedOnly: false,
  description: '场风的刻子',
});

export const YAKUHAI_SEAT = defineYaku({
  id: 'yakuhai_seat',
  name: '自风牌',
  romaji: 'yakuhai seat wind',
  han: 1,
  category: 'honor',
  level: 'normal',
  closedOnly: false,
  description: '自风的刻子',
});

export const HAITEI = defineYaku({
  id: 'haitei',
  name: '海底摸月',
  romaji: 'haitei raoyue',
  han: 1,
  category: 'special',
  level: 'normal',
  closedOnly: false,
  description: '自摸最后一张牌和牌',
});

export const HOUTEI = defineYaku({
  id: 'houtei',
  name: '河底摸鱼',
  romaji: 'houtei raoyui',
  han: 1,
  category: 'special',
  level: 'normal',
  closedOnly: false,
  description: '荣和最后一张弃牌',
});

export const RINSHAN = defineYaku({
  id: 'rinshan',
  name: '岭上开花',
  romaji: 'rinshan kaihou',
  han: 1,
  category: 'special',
  level: 'normal',
  closedOnly: false,
  description: '杠后从岭上摸牌和牌',
});

export const CHANKAN = defineYaku({
  id: 'chankan',
  name: '抢杠',
  romaji: 'chankan',
  han: 1,
  category: 'special',
  level: 'normal',
  closedOnly: false,
  description: '抢他人加杠的牌和牌',
});

// ════════════════════════════════════════════════════════════════
// 2 番役
// ════════════════════════════════════════════════════════════════

export const SANSHOKU_DOUJUN = defineYaku({
  id: 'sanshoku_doujun',
  name: '三色同顺',
  romaji: 'sanshoku doujun',
  han: 2,
  openHan: 1,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '三个花色有相同数字的顺子',
});

export const ITTSU = defineYaku({
  id: 'ittsu',
  name: '一气通贯',
  romaji: 'ittsuu',
  han: 2,
  openHan: 1,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '同一花色的 123+456+789',
});

export const CHANTA = defineYaku({
  id: 'chanta',
  name: '混全带幺九',
  romaji: 'chanta',
  han: 2,
  openHan: 1,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '每个面子都含幺九牌，且有字牌',
});

export const CHIITOITSU = defineYaku({
  id: 'chiitoitsu',
  name: '七对子',
  romaji: 'chiitoitsu',
  han: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: true,
  description: '七个不同的对子',
});

export const TOITOI = defineYaku({
  id: 'toitoi',
  name: '对对和',
  romaji: 'toitoi',
  han: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '四个面子全是刻子',
});

export const SANANKOU = defineYaku({
  id: 'sanankou',
  name: '三暗刻',
  romaji: 'sanankou',
  han: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '三个暗刻（含暗杠）',
});

export const SANSHOKU_DOUKOU = defineYaku({
  id: 'sanshoku_doukou',
  name: '三色同刻',
  romaji: 'sanshoku doukou',
  han: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '三个花色有相同数字的刻子',
});

export const SANKANTSU = defineYaku({
  id: 'sankantsu',
  name: '三杠子',
  romaji: 'sankantsu',
  han: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '三个杠子',
});

export const HONROUTOU = defineYaku({
  id: 'honroutou',
  name: '混老头',
  romaji: 'honroutou',
  han: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '全部由幺九牌组成（含字牌）',
});

export const SHOUSHANGEN = defineYaku({
  id: 'shousangen',
  name: '小三元',
  romaji: 'shousangen',
  han: 2,
  category: 'honor',
  level: 'normal',
  closedOnly: false,
  description: '白发中其中两个是刻子，一个是雀头',
});

// ════════════════════════════════════════════════════════════════
// 3 番役
// ════════════════════════════════════════════════════════════════

export const RYANPEIKOU = defineYaku({
  id: 'ryanpeikou',
  name: '二盃口',
  romaji: 'ryanpeikou',
  han: 3,
  category: 'shape',
  level: 'normal',
  closedOnly: true,
  description: '两组一盃口',
});

export const JUNCHAN = defineYaku({
  id: 'junchan',
  name: '纯全带幺九',
  romaji: 'junchan',
  han: 3,
  openHan: 2,
  category: 'shape',
  level: 'normal',
  closedOnly: false,
  description: '每个面子都含老头牌（1/9），无字牌',
});

export const HONITSU = defineYaku({
  id: 'honitsu',
  name: '混一色',
  romaji: 'honitsu',
  han: 3,
  openHan: 2,
  category: 'suit',
  level: 'normal',
  closedOnly: false,
  description: '单一花色 + 字牌',
});

// ════════════════════════════════════════════════════════════════
// 6 番役
// ════════════════════════════════════════════════════════════════

export const CHINITSU = defineYaku({
  id: 'chinitsu',
  name: '清一色',
  romaji: 'chinitsu',
  han: 6,
  openHan: 5,
  category: 'suit',
  level: 'normal',
  closedOnly: false,
  description: '单一花色，无字牌',
});

// ════════════════════════════════════════════════════════════════
// 役满
// ════════════════════════════════════════════════════════════════

export const KOKUSHI = defineYaku({
  id: 'kokushi',
  name: '国士无双',
  romaji: 'kokushi musou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: true,
  description: '13 种幺九牌各 1 张 + 其中 1 种 2 张',
});

export const KOKUSHI_13 = defineYaku({
  id: 'kokushi_13',
  name: '国士无双十三面',
  romaji: 'kokushi musou juusanmen',
  han: 26,
  category: 'yakuman',
  level: 'double-yakuman',
  closedOnly: true,
  description: '国士无双听 13 面（纯正国士）',
});

export const SUUANKOU = defineYaku({
  id: 'suuankou',
  name: '四暗刻',
  romaji: 'suuankou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: true,
  description: '四个暗刻（含暗杠）',
});

export const SUUANKOU_TANKI = defineYaku({
  id: 'suuankou_tanki',
  name: '四暗刻单骑',
  romaji: 'suuankou tanki',
  han: 26,
  category: 'yakuman',
  level: 'double-yakuman',
  closedOnly: true,
  description: '四暗刻 + 单骑听牌',
});

export const DAISANGEN = defineYaku({
  id: 'daisangen',
  name: '大三元',
  romaji: 'daisangen',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: false,
  description: '白发中三个刻子',
});

export const SHOUSUUSHII = defineYaku({
  id: 'shousuushii',
  name: '小四喜',
  romaji: 'shousuushii',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: false,
  description: '三个风牌刻子 + 一个风牌雀头',
});

export const DAISUUSHII = defineYaku({
  id: 'daisuushii',
  name: '大四喜',
  romaji: 'daisuushii',
  han: 26,
  category: 'yakuman',
  level: 'double-yakuman',
  closedOnly: false,
  description: '四个风牌刻子',
});

export const TSUUIISOU = defineYaku({
  id: 'tsuuiisou',
  name: '字一色',
  romaji: 'tsuuiisou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: false,
  description: '全部是字牌',
});

export const CHINROUTOU = defineYaku({
  id: 'chinroutou',
  name: '清老头',
  romaji: 'chinroutou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: false,
  description: '全部是老头牌（1/9 数牌）',
});

export const RYUUISOU = defineYaku({
  id: 'ryuuiisou',
  name: '绿一色',
  romaji: 'ryuuiisou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: false,
  description: '只含 2s/3s/4s/6s/8s/发',
});

export const CHUUREN = defineYaku({
  id: 'chuuren',
  name: '九莲宝灯',
  romaji: 'chuuren poutou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: true,
  description: '门清单一花色的 1112345678999 形态',
});

export const JUNSEI_CHUUREN = defineYaku({
  id: 'junsei_chuuren',
  name: '纯正九莲宝灯',
  romaji: 'junsei chuuren poutou',
  han: 26,
  category: 'yakuman',
  level: 'double-yakuman',
  closedOnly: true,
  description: '九莲宝灯九面听',
});

export const SUUKANTSU = defineYaku({
  id: 'suukantsu',
  name: '四杠子',
  romaji: 'suukantsu',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: false,
  description: '四个杠子',
});

export const TENHOU = defineYaku({
  id: 'tenhou',
  name: '天和',
  romaji: 'tenhou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: true,
  description: '庄家配牌即和',
});

export const CHIIHOU = defineYaku({
  id: 'chiihou',
  name: '地和',
  romaji: 'chiihou',
  han: 13,
  category: 'yakuman',
  level: 'yakuman',
  closedOnly: true,
  description: '子家第一巡自摸和牌',
});

// ════════════════════════════════════════════════════════════════
// 汇总
// ════════════════════════════════════════════════════════════════

export const ALL_YAKU: readonly YakuDef[] = [
  // 1 番
  RIICHI,
  DOUBLE_RIICHI,
  IPPATSU,
  MENZEN_TSUMO,
  PINFU,
  TANYAO,
  IIPEIKOU,
  YAKUHAI_HAKU,
  YAKUHAI_HATSU,
  YAKUHAI_CHUN,
  YAKUHAI_ROUND,
  YAKUHAI_SEAT,
  HAITEI,
  HOUTEI,
  RINSHAN,
  CHANKAN,
  // 2 番
  SANSHOKU_DOUJUN,
  ITTSU,
  CHANTA,
  CHIITOITSU,
  TOITOI,
  SANANKOU,
  SANSHOKU_DOUKOU,
  SANKANTSU,
  HONROUTOU,
  SHOUSHANGEN,
  // 3 番
  RYANPEIKOU,
  JUNCHAN,
  HONITSU,
  // 6 番
  CHINITSU,
  // 役满
  KOKUSHI,
  KOKUSHI_13,
  SUUANKOU,
  SUUANKOU_TANKI,
  DAISANGEN,
  SHOUSUUSHII,
  DAISUUSHII,
  TSUUIISOU,
  CHINROUTOU,
  RYUUISOU,
  CHUUREN,
  JUNSEI_CHUUREN,
  SUUKANTSU,
  TENHOU,
  CHIIHOU,
];

/** 按 ID 查找役种 */
export function findYaku(id: string): YakuDef | undefined {
  return ALL_YAKU.find((y) => y.id === id);
}

/** 役满列表 */
export const YAKUMAN_LIST: readonly YakuDef[] = ALL_YAKU.filter(
  (y) => y.level !== 'normal',
);
